import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Phone, Mail, Globe, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

// Route mappings for each language
const routeMappings = {
  de: {
    home: '',
    services: 'leistungen',
    drywall: 'trockenbau',
    flooring: 'bodenbelaege',
    repairs: 'kleinreparaturen',
    ecological: 'oekologische-materialien',
    licenseFree: 'zulassungsfreie-taetigkeiten',
    about: 'ueber-mich',
    workProcess: 'arbeitsweise',
    contact: 'kontakt',
    impressum: 'impressum',
    privacy: 'datenschutz',
    cookies: 'cookie-richtlinie',
    disclaimer: 'disclaimer',
    review: 'kundenbewertung',
  },
  en: {
    home: '',
    services: 'services',
    drywall: 'drywall-services',
    flooring: 'flooring-services',
    repairs: 'minor-repairs',
    ecological: 'ecological-materials',
    licenseFree: 'license-free',
    about: 'about',
    workProcess: 'work-process',
    contact: 'contact',
    impressum: 'legal-notice',
    privacy: 'privacy',
    cookies: 'cookie-policy',
    disclaimer: 'disclaimer',
    review: 'customer-review',
  },
  it: {
    home: '',
    services: 'servizi',
    drywall: 'cartongesso',
    flooring: 'pavimenti',
    repairs: 'piccole-riparazioni',
    ecological: 'materiali-ecologici',
    licenseFree: 'attivita-senza-licenza',
    about: 'chi-sono',
    workProcess: 'metodo',
    contact: 'contatti',
    impressum: 'note-legali',
    privacy: 'privacy-policy',
    cookies: 'politica-cookie',
    disclaimer: 'esclusione-responsabilita',
    review: 'recensione',
  },
  es: {
    home: '',
    services: 'servicios',
    drywall: 'tabiqueria-seca',
    flooring: 'suelos',
    repairs: 'pequenas-reparaciones',
    ecological: 'materiales-ecologicos',
    licenseFree: 'actividades-sin-licencia',
    about: 'sobre-mi',
    workProcess: 'proceso',
    contact: 'contacto',
    impressum: 'aviso-legal',
    privacy: 'politica-privacidad',
    cookies: 'politica-cookies',
    disclaimer: 'exencion-responsabilidad',
    review: 'resena',
  },
};

export function useLocalizedPath() {
  const { lang = 'de' } = useParams();
  const currentLang = languages.find(l => l.code === lang) ? lang : 'de';
  const routes = routeMappings[currentLang];
  
  return {
    lang: currentLang,
    routes,
    getPath: (routeKey) => `/${currentLang}/${routes[routeKey]}`.replace(/\/$/, ''),
    homePath: `/${currentLang}`,
  };
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { lang = 'de' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { routes, getPath, homePath } = useLocalizedPath();

  // Get actual current language from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const urlLang = pathParts[0] && ['de', 'en', 'it', 'es'].includes(pathParts[0]) ? pathParts[0] : 'de';
  
  const currentLang = languages.find(l => l.code === urlLang) || languages[0];

  const navigation = [
    { name: t('nav.home'), href: homePath, key: 'home' },
    { name: t('nav.services'), href: getPath('services'), key: 'services' },
    { name: t('nav.licenseFreeTasks'), href: getPath('licenseFree'), key: 'licenseFree' },
    { name: t('nav.aboutMe'), href: getPath('about'), key: 'about' },
    { name: t('nav.workProcess'), href: getPath('workProcess'), key: 'workProcess' },
    { name: t('nav.contact'), href: getPath('contact'), key: 'contact' },
  ];

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
    
    // Get current path from router location (more reliable than window.location)
    const currentPath = location.pathname;
    const currentPathParts = currentPath.split('/').filter(Boolean);
    const currentLangFromUrl = currentPathParts[0];
    
    // If we have a route after the language prefix
    if (currentPathParts.length > 1) {
      const currentRoute = currentPathParts[1];
      let routeKey = null;
      
      // Find the route key for current path in the current language's mappings
      const currentLangMappings = routeMappings[currentLangFromUrl] || routeMappings.de;
      for (const [key, value] of Object.entries(currentLangMappings)) {
        if (value === currentRoute) {
          routeKey = key;
          break;
        }
      }
      
      // If we found a matching route key, navigate to the same page in new language
      if (routeKey && routeMappings[newLang][routeKey]) {
        const newRoute = routeMappings[newLang][routeKey];
        const newPath = newRoute === '' ? `/${newLang}` : `/${newLang}/${newRoute}`;
        navigate(newPath);
      } else {
        // Fallback: go to homepage in new language if route doesn't exist
        navigate(`/${newLang}`);
      }
    } else {
      // We're on the homepage, just change language prefix
      navigate(`/${newLang}`);
    }
  };

  const isActive = (href) => {
    const currentPath = location.pathname;
    if (href === homePath) {
      return currentPath === homePath || currentPath === `${homePath}/`;
    }
    return currentPath.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#2D3748] shadow-sm">
      {/* Top bar */}
      <div className="bg-[#1A202C] text-white py-2">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex justify-between items-center text-sm">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/10 h-8 px-2"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  <span className="font-medium">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[150px]">
                {languages.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`cursor-pointer ${urlLang === language.code ? 'bg-orange-50 text-orange-600' : ''}`}
                    data-testid={`lang-${language.code}`}
                  >
                    <span className="mr-2">{language.flag}</span>
                    <span>{language.name}</span>
                    {urlLang === language.code && (
                      <span className="ml-auto text-orange-500">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Contact info */}
            <div className="flex items-center gap-4 md:gap-6">
              <a
                href="tel:+4915207684235"
                className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                data-testid="header-phone-link"
              >
                <Phone className="h-4 w-4" />
                <span className="hidden sm:inline">+49 152 07684235</span>
              </a>
              <a
                href="mailto:info@mtrenovierung.de"
                className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                data-testid="header-email-link"
              >
                <Mail className="h-4 w-4" />
                <span className="hidden md:inline">info@mtrenovierung.de</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Link to Home */}
          <Link to={homePath} className="flex items-center" data-testid="header-logo">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm md:text-base">MT</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? "text-orange-400 bg-white/10"
                    : "text-white hover:text-orange-400 hover:bg-white/10"
                }`}
                data-testid={`nav-link-${item.key}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden lg:block">
            <Button
              asChild
              className="bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg transition-all"
              data-testid="header-cta-button"
            >
              <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten&body=Guten%20Tag%2C%0A%0AIch%20interessiere%20mich%20für%20Ihre%20Dienstleistungen%20und%20bitte%20um%20ein%20unverbindliches%20Angebot.%0A%0AMit%20freundlichen%20Grüßen">
                {t('nav.requestQuote')}
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
                <span className="sr-only">{t('nav.menu')}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-white">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between py-4 border-b border-gray-200">
                  <span className="text-lg font-bold text-[#2D3748]">{t('nav.menu')}</span>
                </div>

                {/* Mobile Language Selector */}
                <div className="py-4 border-b border-gray-200">
                  <p className="text-sm text-gray-600 mb-2 font-medium">Sprache / Language</p>
                  <div className="flex gap-2">
                    {languages.map((language) => (
                      <Button
                        key={language.code}
                        variant={urlLang === language.code ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          handleLanguageChange(language.code);
                          setIsOpen(false);
                        }}
                        className={urlLang === language.code ? "bg-orange-500 hover:bg-orange-600 text-white font-semibold" : "border-gray-300 text-gray-700 hover:bg-gray-100"}
                        data-testid={`mobile-lang-${language.code}`}
                      >
                        {language.code.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>

                <nav className="flex-1 py-6">
                  <ul className="space-y-1">
                    {navigation.map((item) => (
                      <li key={item.key}>
                        <Link
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                            isActive(item.href)
                              ? "text-orange-600 bg-orange-50"
                              : "text-[#2D3748] hover:text-orange-600 hover:bg-gray-50"
                          }`}
                          data-testid={`mobile-nav-link-${item.key}`}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="py-6 border-t border-gray-200">
                  <Button
                    asChild
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    data-testid="mobile-cta-button"
                  >
                    <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten&body=Guten%20Tag%2C%0A%0AIch%20interessiere%20mich%20für%20Ihre%20Dienstleistungen%20und%20bitte%20um%20ein%20unverbindliches%20Angebot.%0A%0AMit%20freundlichen%20Grüßen">
                      {t('nav.requestQuote')}
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
